import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, InputGroup, Row, Spinner, Table } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { EMPLOYMENT_TYPES, STAFF_STATUS, fmtDateTH } from './hrShared'

// การ์ดบุคลากร 1 คน สำหรับมุมมองมือถือ
const StaffCard = ({ s, onClick }) => {
  const st = STAFF_STATUS[s.hp_status] || STAFF_STATUS.active
  return (
    <div className="border rounded p-3" style={{ cursor: 'pointer' }} onClick={onClick}>
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="fw-semibold text-truncate">{s.t_name}</div>
          <div className="fs-xs text-muted"><code>{s.t_idnumber}</code></div>
        </div>
        <Badge bg={st.bg}>{st.label}</Badge>
      </div>
      <div className="mt-2 pt-2 border-top fs-sm">
        <div><span className="text-muted">ฝ่าย/แผนก:</span> {s.hp_department || '-'}</div>
        <div><span className="text-muted">ตำแหน่ง:</span> {s.hp_position_title || '-'}</div>
        <div><span className="text-muted">ประเภทการจ้าง:</span> {EMPLOYMENT_TYPES[s.hp_employment_type] || '-'}</div>
        <div><span className="text-muted">วันที่เริ่มงาน:</span> {fmtDateTH(s.hp_hire_date)}</div>
      </div>
    </div>
  )
}

export default function HrStaffList() {
  const navigate = useNavigate()
  const [staff, setStaff] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  const fetchStaff = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      if (search) params.set('search', search)
      if (status) params.set('status', status)
      const res = await axiosInstance.get(`/api/hr/staff?${params}`)
      if (res.data.success) setStaff(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลบุคลากรไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [search, status])

  useEffect(() => {
    const timer = setTimeout(fetchStaff, 300)
    return () => clearTimeout(timer)
  }, [fetchStaff])

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="main-title mb-0">
              <i className="ri-team-line me-2 text-primary" />
              ทะเบียนประวัติบุคลากร
            </h4>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          <Card className="card-one mb-3">
            <Card.Body className="p-3">
              <Row className="g-2">
                <Col xs={12} md={6}>
                  <InputGroup size="sm">
                    <InputGroup.Text><i className="ri-search-line" /></InputGroup.Text>
                    <Form.Control
                      placeholder="ค้นหาชื่อ, รหัสประจำตัว, หรือฝ่าย/แผนก"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col xs={6} md={3}>
                  <Form.Select size="sm" value={status} onChange={e => setStatus(e.target.value)}>
                    <option value="">สถานะทั้งหมด</option>
                    {Object.entries(STAFF_STATUS).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={6} md={3}>
                  <Button size="sm" variant="outline-secondary" onClick={() => { setSearch(''); setStatus('') }}>
                    <i className="ri-refresh-line me-1" />ล้างตัวกรอง
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="card-one">
            <Card.Header className="d-flex align-items-center justify-content-between gap-2">
              <Card.Title as="h6" className="mb-0">รายชื่อบุคลากร</Card.Title>
              <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                <button type="button" className={`btn btn${view === 'table' ? '' : '-outline'}-primary`}
                  onClick={() => setView('table')} title="มุมมองตาราง">
                  <i className="ri-table-line" />
                </button>
                <button type="button" className={`btn btn${view === 'card' ? '' : '-outline'}-primary`}
                  onClick={() => setView('card')} title="มุมมองการ์ด">
                  <i className="ri-layout-grid-line" />
                </button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
              ) : staff.length === 0 ? (
                <div className="text-center text-muted py-4">ไม่พบข้อมูลบุคลากร</div>
              ) : view === 'card' ? (
                <div className="p-3">
                  <Row className="g-3">
                    {staff.map(s => (
                      <Col key={s.t_id} xs={12} md={6} xl={4}>
                        <StaffCard s={s} onClick={() => navigate(`/hr/staff/${s.t_id}`)} />
                      </Col>
                    ))}
                  </Row>
                </div>
              ) : (
                <Table responsive hover className="mb-0 fs-sm">
                  <thead className="table-light">
                    <tr>
                      <th>รหัสประจำตัว</th>
                      <th>ชื่อ-สกุล</th>
                      <th>ฝ่าย/แผนก</th>
                      <th>ตำแหน่ง</th>
                      <th>ประเภทการจ้าง</th>
                      <th>วันที่เริ่มงาน</th>
                      <th>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staff.map(s => {
                      const st = STAFF_STATUS[s.hp_status] || STAFF_STATUS.active
                      return (
                        <tr key={s.t_id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/hr/staff/${s.t_id}`)}>
                          <td><code>{s.t_idnumber}</code></td>
                          <td>{s.t_name}</td>
                          <td>{s.hp_department || '-'}</td>
                          <td>{s.hp_position_title || '-'}</td>
                          <td>{EMPLOYMENT_TYPES[s.hp_employment_type] || '-'}</td>
                          <td>{fmtDateTH(s.hp_hire_date)}</td>
                          <td><Badge bg={st.bg}>{st.label}</Badge></td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
      <Footer />
    </>
  )
}
